'use client';

import { Box } from '@mui/material';
import { ReactElement, ReactNode } from 'react';

export type ResendReserveOccupant = Readonly<{
  /** Identifies this occupant. Matched against `liveKey` to decide whether this is the one shown
   *  right now. */
  key: string;
  /** The text (or other node) this occupant holds. Always mounted, whether or not this occupant
   *  is currently live - see the component doc comment for why. */
  content: ReactNode;
}>;

export type ResendReserveProps = Readonly<{
  /** Every text this cell will ever need to hold, each rendered as its own permanently-mounted
   *  grid child (`gridArea: '1 / 1'` on all of them, matching the design board's own `.slot > *`
   *  rule), sized by real layout rather than a hidden ruler - see the component doc comment. */
  occupants: readonly ResendReserveOccupant[];
  /** Which occupant (by `key`) is currently live. `null` means none of them are - the resting
   *  "nothing shown" state - in which case every occupant renders `visibility: hidden` and
   *  `aria-hidden`. Whichever occupant's `key` matches carries neither. */
  liveKey: string | null;
  'data-testid'?: string;
}>;

/**
 * One CSS grid cell holding every text this spot will ever need to show, all mounted at once, in
 * the same grid area (`gridArea: '1 / 1'` on each, matching the "Resend timing options" design
 * board's own `.slot > *` rule directly - see its `.slot` CSS rule and its A1/A2 frames). Exactly
 * one occupant is live at a time (`liveKey`, or none while it is `null`); every other occupant
 * renders `visibility: hidden` AND `aria-hidden`, so it still occupies its own space in the grid
 * cell below but is invisible and inaccessible - PR 4's live region depends on this: the region's
 * accessible content is only ever the live occupant's text. Used twice by LinkSent's own resend
 * composition - once for the line under the resend action (nothing, the pressed-inside-window
 * answer, or the replacement warning), and once for the returning-customer sign-in route's held
 * error space (nothing, or the code-rejected message) - which is why this is its own small
 * component rather than being written out twice.
 *
 * This exists at all because of a rule settled on the "Link sent, one screen" design board
 * (settled 2026-09-17): the screen holds this space from first paint so that a customer's own
 * press never grows or moves the page, and it must be laid out rather than measured, because a
 * hardcoded height would be wrong at one of the two widths this screen ships at - the reserved
 * warning text wraps to two lines at 390 and sits on one line at 600. Grid is what makes "laid
 * out, never measured" true: every occupant is real DOM, sized by the browser's own text layout
 * at whatever width is current, so the cell's own height is simply whichever occupant is tallest
 * - a fact of the layout, not a number this component or its caller had to work out itself.
 *
 * Every occupant being permanently mounted, with only visibility toggling between them, is also
 * what makes "no text duplicated in the DOM" hold by construction, not by a filtering workaround
 * in the caller. An earlier draft paired one always-hidden "ruler" occupant with a second,
 * separately-live child, so the ruler's own text and the live text were the same words rendered
 * twice over whenever the live occupant happened to equal the ruler - callers had to filter by
 * visibility to tell the two apart. Here every occupant IS a grid child, always mounted, and
 * exactly one is ever visible, so there is never a second copy of the same text to filter out.
 *
 * The outer `Box` carries `role="status"` and `aria-live="polite"`, so a screen reader hears the
 * live occupant change (e.g. slot 28 appearing once the throttle window elapses) without the
 * screen stealing focus - WCAG 4.1.3, the "status message" success criterion for content whose
 * meaning changes without a user-initiated context change. This is deliberately not
 * `role="timer"` (WCAG 2.2.2): that role is for an auto-starting, always-present tick, and this
 * region instead changes on a timer-FIRED EVENT (a transition into or out of being live), not on
 * a running clock the AT reads continuously.
 *
 * A polite live region only announces reliably when the region's content genuinely mutates - a
 * real DOM node added, removed, or text-changed - not when an already-mounted node merely has a
 * CSS property or `aria-hidden` flipped. Because every occupant here is permanently mounted (see
 * above), a naive `key={occupant.key}` would never give assistive technology a childList mutation
 * to notice: `liveKey` changing would only flip `visibility`/`aria-hidden` on nodes that were
 * already there. The live occupant is therefore keyed differently from how the same occupant is
 * keyed while not live (`live-${occupant.key}` vs `occupant.key`), so the moment an occupant
 * transitions into or out of being live, ITS key changes between renders and React genuinely
 * unmounts the old node and mounts a fresh one in its place - a real childList mutation, which is
 * what assistive technology reliably announces. This costs nothing visually: the remount happens
 * within the same render/layout pass, and the occupant's content and position are identical
 * before and after.
 *
 * Every non-live occupant staying `aria-hidden` (unaffected by the key change) is what keeps the
 * region's accessible content to only ever be the live occupant's text - the live region would
 * otherwise announce every occupant's text at once, live or not.
 */
export function ResendReserve({
  occupants,
  liveKey,
  'data-testid': dataTestId,
}: ResendReserveProps): ReactElement {
  return (
    <Box
      data-testid={dataTestId}
      role="status"
      aria-live="polite"
      sx={{ display: 'grid', width: '100%' }}
    >
      {occupants.map((occupant) => {
        const isLive = occupant.key === liveKey;
        return (
          <Box
            key={isLive ? `live-${occupant.key}` : occupant.key}
            aria-hidden={isLive ? undefined : true}
            sx={{ gridArea: '1 / 1', visibility: isLive ? 'visible' : 'hidden' }}
          >
            {occupant.content}
          </Box>
        );
      })}
    </Box>
  );
}
